import { TYPING_WINDOW_MS, type BusyProbe } from './autoUpdateModel';

// D-341 (3): DOM probes for "is the user in the middle of something".
let lastKeyAt = 0;
let listening = false;

/** Start remembering the last key press (idempotent). */
export function watchTyping(): void {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  window.addEventListener('keydown', () => { lastKeyAt = Date.now(); }, { capture: true, passive: true });
}

function focusedFieldHasText(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  if (el.isContentEditable) return (el.textContent || '').trim().length > 0;
  if (el instanceof HTMLTextAreaElement) return el.value.trim().length > 0;
  if (el instanceof HTMLInputElement) {
    const t = el.type;
    if (['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes(t)) return false;
    return el.value.trim().length > 0;
  }
  return false;
}

/** A form with unsaved changes registers a `beforeunload` guard; a cancelable synthetic event shows whether anyone objects. */
function unsavedGuardActive(): boolean {
  try {
    const ev = new Event('beforeunload', { cancelable: true });
    window.dispatchEvent(ev);
    return ev.defaultPrevented;
  } catch {
    return false;
  }
}

const NON_TEXT_INPUTS = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file', 'hidden', 'image']);

/**
 * QA-PSCOPE-ENGAGE: ANY field on the page holding text counts, focused or not (a comment half-written in a ticket page,
 * then the user looked away). A controlled React field keeps its value attribute in sync, so "differs from the initial
 * value" cannot be told apart from the DOM: any non-empty editable field is treated as work in progress.
 */
export function anyFieldHasText(root: ParentNode = document): boolean {
  const fields = root.querySelectorAll<HTMLElement>('textarea, input, [contenteditable=""], [contenteditable="true"]');
  for (const el of Array.from(fields)) {
    if (el instanceof HTMLTextAreaElement) {
      if (!el.readOnly && !el.disabled && el.value.trim()) return true;
    } else if (el instanceof HTMLInputElement) {
      if (!NON_TEXT_INPUTS.has(el.type) && !el.readOnly && !el.disabled && el.value.trim()) return true;
    } else if ((el.textContent || '').trim()) {
      return true;
    }
  }
  return false;
}

export function probeBusy(): BusyProbe {
  return {
    overlayOpen: !!document.querySelector('[data-overlay-layer] [aria-modal="true"]'),
    typing: focusedFieldHasText() || Date.now() - lastKeyAt < TYPING_WINDOW_MS,
    unsavedChanges: unsavedGuardActive(),
    fieldsWithText: anyFieldHasText(),
  };
}
